import { Injectable } from '@angular/core';
import {
    ValueMetricPaywallType,
    ValueMetricsProductType
} from '../../constants-types';
import { ModuleName, PartnerInformation } from '../../core-data-model';
import { GetValueMetric } from '../../core-data-model/ValueMetrics';
import {
    ValueMetricsModalComponent,
    ValueMetricsPaywallModalComponent,
    ValueMetricsPaywallModalConfig
} from '../../core-ui';
import { ModalsService } from '../../modals';
import {
    ModuleAccessService,
    UserProfileSettingsService
} from '../../system-services';
import { ValueMetricsService } from './value-metrics.service';

@Injectable({
    providedIn: 'root'
})
export class ValueMetricsPaywallService {
    totalUsedSessions: number;
    partnerInfo: PartnerInformation;
    modalIsProcessingEvent = false;

    constructor(
        private moduleAccess: ModuleAccessService,
        private modalService: ModalsService,
        private vmService: ValueMetricsService,
        private userProfile: UserProfileSettingsService
    ) {}

    hasUnlimitedValueMetrics(metricName: string, module: ModuleName) {
        return (
            this.moduleAccess.getValueMetricForProduct(metricName, module) ===
            -1
        );
    }

    /**
     * Displays a paywall based on configuration and available VMs
     * Provided callbacks will be called when a user
     * clicks to confirm consumption of VMs and will be given the emitted confirmation
     * boolean from the modal components themselves.
     *
     * @param paywallModalConfig
     * @param noVMAvailableConfirmationCallback
     * @param hasVMAvailableConfirmationCallback
     * @param convertSelfServeCallback
     * @param hasSelfServeVMAvailableCallback
     */
    async showPaywallModal(
        paywallModalConfig: ValueMetricsPaywallModalConfig,
        noVMAvailableConfirmationCallback: () => void,
        hasVMAvailableConfirmationCallback: (
            willConsumeVM?: boolean
        ) => Promise<void>,
        convertSelfServeCallback: () => void,
        hasSelfServeVMAvailableCallback: (
            willConsumeVM?: boolean
        ) => Promise<void>
    ) {
        const paywallType = await this.getPaywallType(
            paywallModalConfig.module,
            paywallModalConfig.metricName,
            paywallModalConfig.metricsConsumed,
            this.moduleAccess.isProductSelfServe(paywallModalConfig.module)
        );

        switch (paywallType) {
            case ValueMetricPaywallType.OutOfValueMetrics:
                this.openNoVMAvailableModal(
                    paywallModalConfig,
                    noVMAvailableConfirmationCallback
                );
                break;
            case ValueMetricPaywallType.AvailableValueMetrics:
                this.openHasVMsAvailableModal(
                    paywallModalConfig,
                    hasVMAvailableConfirmationCallback
                );
                break;
            case ValueMetricPaywallType.ConvertSelfServe:
                this.openConvertSelfServeModal(
                    paywallModalConfig,
                    convertSelfServeCallback
                );
                break;
            case ValueMetricPaywallType.SelfServeAvailableValueMetrics:
                this.openSelfServeSessionsAvailableModal(
                    paywallModalConfig,
                    hasSelfServeVMAvailableCallback
                );
                break;
            default:
                // TODO: Need better default?
                break;
        }
    }

    /**
     * Returns whether given metric has >0 available
     *
     * @param module
     * @param metricName
     * @returns
     */
    async hasValueMetricsAvailable(
        module: ModuleName,
        metricName: string
    ): Promise<boolean> {
        const vmConfig = await this.getValueMetricConfig(module, metricName);

        if (vmConfig.available > 0) {
            return true;
        }
        return false;
    }

    async getValueMetricConfig(module: ModuleName, metricName: string) {
        const request: GetValueMetric = {
            productType: this.moduleNameToProductType(module),
            name: metricName,
            companyId: this.userProfile.currentCompany
        };

        return this.vmService.getValueMetricConfig(request);
    }

    private moduleNameToProductType(module: ModuleName) {
        switch (module) {
            case ModuleName.Design:
                return ValueMetricsProductType.Design;
            case ModuleName.HireHero:
                return ValueMetricsProductType.HireHero;
            case ModuleName.Diagnose:
                return ValueMetricsProductType.Diagnose;
            case ModuleName.Hire:
                return ValueMetricsProductType.Hire;
            case ModuleName.Inspire:
                return ValueMetricsProductType.Inspire;
            case ModuleName.Design2:
                return ValueMetricsProductType.Design2;
            default:
                return ValueMetricsProductType.Unknown;
        }
    }

    async getTotalAvailableValueMetrics(
        module: ModuleName,
        metricName: string
    ): Promise<number> {
        // Update counts to get the latest avalailable VM count
        await this.moduleAccess.fetchConfig();
        return this.moduleAccess.getValueMetricForProduct(metricName, module);
    }

    async getPaywallType(
        module: ModuleName,
        metricName: string,
        metricsConsumed: number,
        isSelfServe?: boolean
    ): Promise<ValueMetricPaywallType> {
        /**
         * This function currently requires integrators to provide
         * the number of VMs consumed, meaning that must be calculated
         * before attempting to show a paywall modal. Ideally this will also be
         * handled generically by the VM Platform
         */
        const vmConfig = await this.getValueMetricConfig(module, metricName);

        const unusedValueMetrics = vmConfig.limit - metricsConsumed;

        /**
         * This logic assumes that all VMs & VM consumption occurs at a
         * 1:1 ratio, and may need to be refactored in the future if that
         * changes
         */
        if (isSelfServe && unusedValueMetrics <= 0) {
            return ValueMetricPaywallType.ConvertSelfServe;
        } else if (isSelfServe && unusedValueMetrics > 0) {
            return ValueMetricPaywallType.SelfServeAvailableValueMetrics;
        } else if (
            unusedValueMetrics > 0 ||
            this.hasUnlimitedValueMetrics(metricName, module)
        ) {
            return ValueMetricPaywallType.AvailableValueMetrics;
        } else if (
            unusedValueMetrics <= 0 &&
            !this.hasUnlimitedValueMetrics(metricName, module)
        ) {
            return ValueMetricPaywallType.OutOfValueMetrics;
        } else {
            return ValueMetricPaywallType.Unknown;
        }
    }

    private openNoVMAvailableModal(
        paywallModalConfig: ValueMetricsPaywallModalConfig,
        noVMAvailableConfirmationCallback: () => void
    ) {
        this.modalService.openModal('value-metrics-paywall-modal', {
            className: 'value-metrics-modal',
            TRtitle: null,
            body: {
                type: ValueMetricsPaywallModalComponent,
                properties: {
                    config: paywallModalConfig,
                    paywallType: ValueMetricPaywallType.OutOfValueMetrics
                },
                events: {
                    confirm: () => {
                        this.modalService.closeModal(
                            'value-metrics-paywall-modal'
                        );
                        noVMAvailableConfirmationCallback();
                    }
                }
            }
        });
    }

    private openHasVMsAvailableModal(
        paywallModalConfig: ValueMetricsPaywallModalConfig,
        hasVMAvailableConfirmationCallback: (
            willConsumeVM?: boolean
        ) => Promise<void>
    ) {
        this.modalService.openModal('value-metrics-modal', {
            className: 'value-metrics-modal',
            TRtitle: null,
            body: {
                type: ValueMetricsModalComponent,
                properties: {
                    bodyText: paywallModalConfig.bodyTextMap.get(
                        ValueMetricPaywallType.AvailableValueMetrics
                    )
                },
                events: {
                    confirm: (emitted: boolean) => {
                        /**
                         * Force this function to return if user clicks to
                         * confirm consumption of VM while modal is still processing
                         * async actions
                         */
                        if (this.modalIsProcessingEvent) {
                            return;
                        }
                        this.modalIsProcessingEvent = true;
                        hasVMAvailableConfirmationCallback(emitted).then(() => {
                            this.modalService.closeModal('value-metrics-modal');
                            this.modalIsProcessingEvent = false;
                        });
                    }
                }
            }
        });
    }

    private openConvertSelfServeModal(
        paywallModalConfig: ValueMetricsPaywallModalConfig,
        convertSelfServeCallback: () => void
    ) {
        this.modalService.openModal('value-metrics-selfServe-paywall-modal', {
            className: 'value-metrics-modal',
            TRtitle: null,
            body: {
                type: ValueMetricsPaywallModalComponent,
                properties: {
                    config: paywallModalConfig,
                    paywallType: ValueMetricPaywallType.ConvertSelfServe
                },
                events: {
                    confirm: () => {
                        this.modalService.closeModal(
                            'value-metrics-selfServe-paywall-modal'
                        );
                        convertSelfServeCallback();
                    }
                }
            }
        });
    }

    private openSelfServeSessionsAvailableModal(
        paywallModalConfig: ValueMetricsPaywallModalConfig,
        callback: (consumeVM?: boolean) => Promise<void>
    ) {
        this.modalService.openModal('value-metrics-modal', {
            className: 'value-metrics-modal',
            TRtitle: null,
            body: {
                type: ValueMetricsModalComponent,
                properties: {
                    bodyText: paywallModalConfig.bodyTextMap.get(
                        ValueMetricPaywallType.SelfServeAvailableValueMetrics
                    )
                },
                events: {
                    confirm: (emitted: boolean) => {
                        if (this.modalIsProcessingEvent) {
                            return;
                        }

                        callback(emitted).then(() => {
                            this.modalService.closeModal('value-metrics-modal');
                            this.modalIsProcessingEvent = false;
                        });
                    }
                }
            }
        });
    }
}
